import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import type { ComponentType, FC, ReactNode } from "react";
import { useState } from "react";
import { ChevronRightIcon } from "../icons";
import type { IconWithBackgroundProps } from "../icons";
import { HACK_alignIconWithLabel } from "@privacy/ui";

type GoToSubDrawerButtonProps = {
  Icon?: ComponentType<IconWithBackgroundProps>;
  label: string | React.ReactNode;
  count?: number;
  onClick: () => void;
  onMouseOver?: () => void;
};

export const GoToSubDrawerButton: FC<GoToSubDrawerButtonProps> = ({ Icon, count, label, onClick, onMouseOver }) => {
  /** We want to fade in the count it inital value is not defined */
  const [initialCount] = useState(count);

  return (
    <GoToSubDrawerButtonBase onClick={onClick}>
      <Stack
        onMouseOver={onMouseOver}
        direction="row"
        sx={{ alignItems: "center", fontSize: theme => theme.typography.pxToRem(14) }}
      >
        {Icon && (
          <Box sx={{ mr: 2 }}>
            <Icon size="sm" />
          </Box>
        )}
        {typeof label === "string" ? (
          <Typography sx={{ fontSize: "inherit", fontWeight: "600", ...HACK_alignIconWithLabel }}>{label}</Typography>
        ) : (
          label
        )}
        {typeof count === "number" && (
          <Typography
            sx={{
              fontSize: "inherit",
              fontWeight: "400",
              ml: 1,
              ...HACK_alignIconWithLabel,
              ...(typeof initialCount === "number"
                ? undefined
                : {
                    "opacity": 0,
                    "animation": "fadein 250ms ease-in forwards",
                    "@keyframes fadein": { from: { opacity: 0 }, to: { opacity: 1 } },
                  }),
            }}
          >
            ({count})
          </Typography>
        )}
      </Stack>
    </GoToSubDrawerButtonBase>
  );
};

export const GoToSubDrawerButtonBase = ({
  children,
  ...props
}: {
  children?: ReactNode;
  onClick?: () => void;
  onMouseEnter?: () => void;
}) => {
  return (
    <Box
      {...props}
      component="button"
      sx={{
        "fontFamily": "inherit",
        "color": theme => theme.palette.text.primary,
        "bgcolor": "transparent",
        "display": "flex",
        "borderRadius": 1,
        "border": "1px solid",
        "borderColor": theme => theme.palette.grey["400"],
        "alignItems": "center",
        "justifyContent": "space-between",
        "p": 2,
        "cursor": "pointer",
        "&:hover": { backgroundColor: theme => theme.palette.action.hover },
        "&:focus-visible": theme => ({
          borderColor: "transparent",
          outline: `2px solid ${theme.palette.primary["400"]}`,
        }),
      }}
    >
      {children}
      <Box sx={{ ...HACK_alignIconWithLabel, opacity: 0.7 }}>
        <ChevronRightIcon size={14} color={theme => theme.palette.text.tertiary} />
      </Box>
    </Box>
  );
};

export const GoToSubDrawerButtonsWrapper: FC<{ children: ReactNode }> = ({ children }) => {
  return <Stack sx={{ gap: 1, pt: 3 }}>{children}</Stack>;
};
